import { useMemo, useState } from 'react';
import { Button, cn } from '@playerbase/ui';

// Reusable collapsible code block with light per-language syntax
// highlighting. Snippets in this section are long-ish (40-100 lines) and
// the typical user copies / downloads them rather than reading every
// line - so we render only the first ~8 lines by default with a fade,
// and let the user expand if they want to skim before copying.

export type SnippetLang = 'gdscript' | 'csharp' | 'cpp' | 'javascript' | 'http';

interface SnippetBlockProps {
  code: string;
  lang: SnippetLang;
  // Maximum lines visible while collapsed. The block stays uncollapsed
  // (no toggle) if the snippet is shorter than this.
  collapsedLines?: number;
}

const COLLAPSED_DEFAULT = 8;

export function SnippetBlock({
  code,
  lang,
  collapsedLines = COLLAPSED_DEFAULT,
}: SnippetBlockProps): JSX.Element {
  const lines = code.split('\n');
  const collapsible = lines.length > collapsedLines + 2;
  const [expanded, setExpanded] = useState(false);

  const tokens = useMemo(() => tokenise(code, lang), [code, lang]);

  return (
    <div className="overflow-hidden rounded-md border border-border bg-[#0b0d10]">
      <div className="relative">
        <div
          className={cn(
            'overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            collapsible && !expanded ? 'max-h-[12.5rem]' : undefined,
          )}
        >
          <pre className="p-4 font-mono text-xs leading-relaxed text-[#cdd6e0]">
            <code>
              {tokens.map((tok, i) => (
                <span key={i} className={TOKEN_CLASS[tok.type]}>
                  {tok.text}
                </span>
              ))}
            </code>
          </pre>
        </div>
        {collapsible && !expanded ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0b0d10] to-transparent" />
        ) : null}
      </div>
      {collapsible ? (
        <div className="flex items-center justify-between border-t border-border/60 bg-[#0b0d10] px-3 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
            {lines.length} lines
          </span>
          <Button variant="ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Collapse' : 'Show all'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tokeniser.
//
// Minimal per-language highlighting: comments, strings, numbers, and a
// small set of keywords. We tokenise on the full string and render each
// token as a coloured <span>. Anything we don't recognise falls through
// as plain text in the base colour. Not a full parser - good enough to
// give studios the visual cue that they're looking at gdscript / c# /
// c++ / bash without dragging in prismjs.
// ---------------------------------------------------------------------------

type TokenType = 'plain' | 'comment' | 'string' | 'number' | 'keyword' | 'attribute';

const TOKEN_CLASS: Record<TokenType, string> = {
  plain: '',
  comment: 'text-[#6a737d] italic',
  string: 'text-[#a8e063]',
  number: 'text-[#ffaa44]',
  keyword: 'text-[#66b3ff]',
  attribute: 'text-[#c792ea]',
};

interface Token {
  type: TokenType;
  text: string;
}

const KEYWORDS: Record<SnippetLang, ReadonlySet<string>> = {
  gdscript: new Set([
    'func', 'var', 'const', 'class_name', 'extends', 'if', 'else', 'elif',
    'for', 'while', 'return', 'await', 'signal', 'pass', 'true', 'false',
    'null', 'self', 'in', 'and', 'or', 'not', 'is', 'as', 'match', 'enum',
    'static', 'export',
  ]),
  csharp: new Set([
    'using', 'namespace', 'class', 'struct', 'public', 'private',
    'protected', 'internal', 'static', 'readonly', 'void', 'async',
    'await', 'var', 'if', 'else', 'for', 'foreach', 'while', 'return',
    'new', 'null', 'true', 'false', 'this', 'string', 'int', 'bool',
    'float', 'double', 'override', 'virtual', 'sealed', 'partial',
  ]),
  cpp: new Set([
    'namespace', 'class', 'struct', 'public', 'private', 'protected',
    'void', 'int', 'bool', 'float', 'double', 'auto', 'const', 'static',
    'if', 'else', 'for', 'while', 'return', 'new', 'nullptr', 'true',
    'false', 'this', 'template', 'typename', 'inline', 'virtual',
    'override', 'using',
  ]),
  javascript: new Set([
    'const', 'let', 'var', 'function', 'class', 'extends', 'static',
    'async', 'await', 'return', 'if', 'else', 'for', 'while', 'do',
    'switch', 'case', 'break', 'continue', 'new', 'this', 'typeof',
    'instanceof', 'in', 'of', 'true', 'false', 'null', 'undefined',
    'import', 'export', 'from', 'as', 'default', 'try', 'catch',
    'finally', 'throw', 'get', 'set',
  ]),
  http: new Set(['POST', 'GET', 'PUT', 'DELETE', 'PATCH', 'HTTP']),
};

function tokenise(source: string, lang: SnippetLang): Token[] {
  const out: Token[] = [];
  const length = source.length;
  let i = 0;
  let plainStart = 0;

  function pushPlain(end: number): void {
    if (end > plainStart) {
      out.push({ type: 'plain', text: source.slice(plainStart, end) });
    }
  }

  const slashStyle =
    lang === 'csharp' || lang === 'cpp' || lang === 'javascript';
  const lineCommentPrefix = slashStyle ? '//' : '#';

  while (i < length) {
    const ch = source[i];
    const two = source.slice(i, i + 2);

    if ((lang === 'cpp' || lang === 'javascript') && two === '/*') {
      pushPlain(i);
      const end = source.indexOf('*/', i + 2);
      const stop = end === -1 ? length : end + 2;
      out.push({ type: 'comment', text: source.slice(i, stop) });
      i = stop;
      plainStart = i;
      continue;
    }

    if (
      (lineCommentPrefix === '//' && two === '//')
      || (lineCommentPrefix === '#' && ch === '#'
          // Avoid eating cpp preprocessor lines if we ever flip langs.
          && lang !== 'cpp')
    ) {
      pushPlain(i);
      const nl = source.indexOf('\n', i);
      const stop = nl === -1 ? length : nl;
      out.push({ type: 'comment', text: source.slice(i, stop) });
      i = stop;
      plainStart = i;
      continue;
    }

    if (ch === '"' || ch === "'") {
      pushPlain(i);
      const quote = ch;
      let j = i + 1;
      while (j < length) {
        if (source[j] === '\\') {
          j += 2;
          continue;
        }
        if (source[j] === quote) {
          j += 1;
          break;
        }
        if (source[j] === '\n') break;
        j += 1;
      }
      out.push({ type: 'string', text: source.slice(i, j) });
      i = j;
      plainStart = i;
      continue;
    }

    if (ch !== undefined && /[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < length) {
        const c = source[j];
        if (c === undefined || !/[A-Za-z0-9_]/.test(c)) break;
        j += 1;
      }
      const word = source.slice(i, j);
      if (KEYWORDS[lang].has(word)) {
        pushPlain(i);
        out.push({ type: 'keyword', text: word });
        plainStart = j;
      }
      i = j;
      continue;
    }

    if (ch !== undefined && /[0-9]/.test(ch)) {
      let j = i + 1;
      while (j < length) {
        const c = source[j];
        if (c === undefined || !/[0-9.]/.test(c)) break;
        j += 1;
      }
      pushPlain(i);
      out.push({ type: 'number', text: source.slice(i, j) });
      i = j;
      plainStart = i;
      continue;
    }

    i += 1;
  }

  pushPlain(length);
  return out;
}
